import { Link, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Container, PillButton, SectionHeading } from "@/components/common/ui";
import { EnquiryForm } from "@/components/forms/EnquiryForm";
import { MortgageCalculator } from "@/components/calculators/MortgageCalculator";
import { VaLoanFacts } from "@/components/military/VaLoanFacts";
import { EstimateTool } from "@/components/guides/EstimateTool";
import { HomeValueTool } from "@/components/guides/HomeValueTool";
import { GuideBlocks } from "@/components/guides/GuideBlocks";
import { GUIDES, guideBySlug } from "@/lib/guides";
import NotFoundPage from "@/pages/NotFoundPage";

const toForm = () => document.getElementById("guide-form")?.scrollIntoView({ behavior: "smooth", block: "start" });

const GuideHero = ({ g }) => (
  <section className="border-b border-neutral-200">
    <Container className="grid gap-10 py-14 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:py-20">
      <div>
        <p className="eyebrow" data-testid="guide-eyebrow">{g.eyebrow}</p>
        <h1 className="mt-4 font-display text-4xl font-normal leading-[1.05] sm:text-5xl lg:text-6xl" data-testid="guide-title">{g.title}</h1>
        <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-neutral-600">{g.intro}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <button type="button" onClick={toForm} data-testid="guide-cta"
            className="inline-flex h-12 items-center rounded-full bg-ink px-6 text-[15px] font-medium text-white transition-colors hover:bg-neutral-800">
            {g.forms[0].title}
          </button>
          {g.links[0]?.to && <PillButton to={g.links[0].to} tone="outline" testId="guide-cta-secondary">{g.links[0].label}</PillButton>}
        </div>
      </div>
      <img src={g.img} alt="" className="aspect-[4/3] w-full rounded-md object-cover" data-testid="guide-hero-img" />
    </Container>
  </section>
);

const Tool = ({ g }) => {
  if (!g.tool) return null;
  const inner = {
    value: <HomeValueTool />, rent: <EstimateTool kind="rent" />,
    va: <VaLoanFacts />, mortgage: <MortgageCalculator defaultLoan="conventional" showBah testId="guide-calculator" />,
  }[g.tool];
  return <section id="estimate" className="scroll-mt-20 bg-mist py-14 sm:py-16" data-testid="guide-tool"><Container>{inner}</Container></section>;
};

const Links = ({ g }) => (
  <div className="flex flex-wrap gap-2" data-testid="guide-links">
    {g.links.map((l, i) => (l.href ? (
      <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" data-testid={`guide-link-${i}`}
        className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-[14px] text-ink transition-colors hover:border-ink">
        {l.label} <ArrowUpRight className="h-3.5 w-3.5" />
      </a>
    ) : (
      <Link key={l.label} to={l.to} data-testid={`guide-link-${i}`} className="rounded-full border border-neutral-300 px-4 py-2 text-[14px] text-ink transition-colors hover:border-ink">{l.label}</Link>
    )))}
  </div>
);

const Forms = ({ g }) => (
  <section id="guide-form" className="scroll-mt-20 py-16 sm:py-20" data-testid="guide-forms">
    <Container className={`grid gap-8 ${g.forms.length > 1 ? "lg:grid-cols-2" : "lg:grid-cols-[1fr_1.3fr]"}`}>
      {g.forms.length === 1 && (
        <div>
          <SectionHeading eyebrow="Talk to The Rob Resh Team" title={g.forms[0].title} subtitle="Short form, real people. We usually reply the same day." />
          <div className="mt-8"><Links g={g} /></div>
        </div>
      )}
      {g.forms.map((f) => (
        <div key={f.kind} className="rounded-md border border-neutral-200 p-6 sm:p-8">
          {g.forms.length > 1 && <p className="mb-5 text-xl font-medium text-ink">{f.title}</p>}
          <EnquiryForm kind={f.kind} topic={f.topic || g.slug} extras={f.extras || []} submitLabel={f.submit}
            messagePlaceholder={f.placeholder} testIdPrefix={`guide-${f.prefix || "form"}`} />
        </div>
      ))}
    </Container>
    {g.forms.length > 1 && <Container className="mt-8"><Links g={g} /></Container>}
    {g.disclaimer && <Container><p className="mt-8 text-[13px] leading-relaxed text-neutral-500" data-testid="guide-disclaimer">{g.disclaimer}</p></Container>}
  </section>
);

const OtherGuides = ({ slug }) => (
  <section className="border-t border-neutral-200 py-12" data-testid="guide-others">
    <Container>
      <p className="eyebrow mb-4">More guides</p>
      <div className="flex flex-wrap gap-2">
        {GUIDES.filter((x) => x.slug !== slug).map((x) => (
          <Link key={x.slug} to={`/guides/${x.slug}`} data-testid={`guide-other-${x.slug}`}
            className="rounded-full bg-mist px-4 py-2 text-[14px] text-ink transition-colors hover:bg-ink hover:text-white">{x.nav}</Link>
        ))}
      </div>
    </Container>
  </section>
);

export default function GuidePage() {
  const { slug } = useParams();
  const g = guideBySlug(slug);
  if (!g) return <NotFoundPage />;
  return (
    <div data-testid={`guide-page-${g.slug}`}>
      <GuideHero g={g} />
      <Tool g={g} />
      <GuideBlocks blocks={g.blocks} slug={g.slug} />
      <Forms g={g} />
      <OtherGuides slug={g.slug} />
    </div>
  );
}
